// ════════════════════════════════════════════════════════════════════════
//  Was der Import prüft, BEVOR er schreibt
//
//  Die ganze Prüfung ist eine reine Funktion: Datei hinein, Zustand der
//  Zielumgebung hinein, je Lehrgang ein Befund heraus. Die Vorschau zeigt nur
//  an, was hier herauskommt, und das Schreiben nimmt daraus, was gewählt wurde.
//
//  Der Unterschied zwischen den drei Sorten Befund ist wichtig:
//
//  - **Sperre** – dieser Lehrgang kommt nicht an. Entweder wiese die Datenbank
//    ihn ohnehin ab (Slug, offener Entwurf), oder er richtete Schaden an, den
//    danach niemand mehr sieht (geteilte Schlüssel).
//  - **Warnung** – er kommt an, aber jemand sollte es gelesen haben.
//  - **Hinweiszahl** – `checkBundle` und `checkEditorial`. Ein ENTWURF darf
//    Hinweise haben; sie sperren erst das Veröffentlichen, und dafür gibt es die
//    Fassungsleiste. Der Import ist kein zweiter Weg daneben.
// ════════════════════════════════════════════════════════════════════════

import { checkBundle, checkKeysKept, parseShape, type Bundle } from "@otto/lehrgang";
import { keysOf } from "../../../shared/lib/contentKeys.js";
import { checkEditorial } from "../../validation/lib/editorialChecks.js";
import { berlinDate, type TransferFile } from "./transferFile.js";

/**
 * Ein Lehrgang, wie ihn die ZIELUMGEBUNG kennt.
 *
 * `keys` sind die Schlüssel ALLER veröffentlichten Fassungen dieses Lehrgangs,
 * nicht nur der neuesten: an einem Schlüssel aus Fassung 3 hängt Fortschritt,
 * auch wenn Fassung 7 ihn nicht mehr führt.
 */
export type LiveCurriculum = {
  readonly id: string;
  readonly slug: string;
  readonly hasDraft: boolean;
  /** `max(version) + 1` – die Nummer, die der neue Entwurf bekommt. */
  readonly nextVersion: number;
  readonly keys: ReadonlySet<string>;
  /** Die zuletzt veröffentlichte Fassung, für `checkKeysKept`. Null, wenn es keine gibt. */
  readonly publishedBundle: Bundle | null;
  /** Ihre Nummer – nur, damit die Warnung sie nennen kann. */
  readonly publishedVersion: number | null;
};

export type ImportEntry = {
  readonly slug: string;
  readonly title: string;
  readonly subtitle: string | null;
  readonly position: number;
  readonly requiresSlug: string | null;
  readonly sourceVersion: number;
  readonly sourcePublishedAt: string;
  /** Null, wenn die Form nicht stimmt – dann ist der Eintrag ohnehin gesperrt. */
  readonly bundle: Bundle | null;
  readonly blockers: readonly string[];
  readonly warnings: readonly string[];
  /** `checkBundle` + `checkEditorial`. Kein Grund zu sperren, siehe Kopf. */
  readonly hints: number;
  /** Gibt es diesen Lehrgang in der Zielumgebung noch nicht? */
  readonly isNew: boolean;
  readonly nextVersion: number;
  /** Der Herkunftsvermerk, der als Notiz am Entwurf steht. */
  readonly notes: string;
};

/** Übernehmbar ist, was keine Sperre hat. */
export function isImportable(entry: ImportEntry): entry is ImportEntry & {
  readonly bundle: Bundle;
} {
  return entry.blockers.length === 0 && entry.bundle !== null;
}

/**
 * Der Plan für einen Import.
 *
 * @param file Die gelesene Datei.
 * @param live Alle Lehrgänge der Zielumgebung – auch die stillgelegten. Ein
 *             stillgelegter Lehrgang ist immer noch da: sein Slug ist vergeben,
 *             und an seinen Schlüsseln hängt Fortschritt.
 */
export function planImport(
  file: TransferFile,
  live: readonly LiveCurriculum[],
): readonly ImportEntry[] {
  const bySlug = new Map(live.map((c) => [c.slug, c]));

  // Erst alle Bundles lesen, dann prüfen: die Kollisionsprüfung innerhalb der
  // Datei braucht die Schlüssel der ANDEREN Einträge, und die stehen erst fest,
  // wenn alle einmal durch `parseShape` gelaufen sind.
  const shapes = file.curricula.map((entry) => {
    const checked = parseShape(entry.bundle);
    return {
      entry,
      bundle: "bundle" in checked ? checked.bundle : null,
      shapeProblems: "problems" in checked ? checked.problems : [],
    };
  });

  return shapes.map(({ entry, bundle, shapeProblems }) => {
    const target = bySlug.get(entry.slug) ?? null;
    const blockers: string[] = [];
    const warnings: string[] = [];

    if (bundle === null) {
      // Die ersten drei genügen: eine kaputte Form erzeugt gern dreißig
      // Meldungen, und wer sie alle liest, findet die erste nicht mehr.
      const gezeigt = shapeProblems.slice(0, 3).map((p) => `${p.path}: ${p.message}`);
      blockers.push(
        `Das Bundle hat nicht die erwartete Form (${gezeigt.join("; ")}` +
          `${shapeProblems.length > 3 ? " …" : ""}).`,
      );
    } else if (bundle.slug !== entry.slug) {
      // Ein Trigger in der Datenbank wacht darüber, dass `content->>'slug'` und
      // `curricula.slug` dasselbe sagen; er wiese das ohnehin ab. Hier steht es
      // vor dem Schreiben und mit einem Grund.
      blockers.push(
        `Im Bundle steht das Kürzel „${bundle.slug}“, im Eintrag „${entry.slug}“. ` +
          `Beide müssen dasselbe sagen – das Kürzel steht im Dateipfad der ` +
          `veröffentlichten Fassungen.`,
      );
    }

    const own = bundle ? keysOf(bundle) : new Set<string>();

    if (bundle) {
      // ── Kollision mit einem ANDEREN Lehrgang der Zielumgebung ───────────
      const fremd = live.filter((c) => c.slug !== entry.slug);
      const kollision = [...own].filter((key) => fremd.some((c) => c.keys.has(key)));
      if (kollision.length > 0) {
        blockers.push(
          `${kollision.length} Schlüssel werden dort schon von einem anderen Lehrgang ` +
            `benutzt (${kollision.slice(0, 3).join(", ")}). Beide teilten sich damit den ` +
            `Fortschritt der Kinder.`,
        );
      }

      // ── Kollision INNERHALB der Datei ───────────────────────────────────
      //
      // Ein Export erzeugt das nie. Eine von Hand bearbeitete Datei schon –
      // und genau dann fällt es sonst niemandem auf.
      const doppelt = shapes
        .filter((other) => other.entry.slug !== entry.slug)
        .flatMap((other) =>
          other.bundle === null
            ? []
            : [...keysOf(other.bundle)].filter((key) => own.has(key)),
        );
      if (doppelt.length > 0) {
        blockers.push(
          `${doppelt.length} Schlüssel stehen in dieser Datei bei zwei Lehrgängen ` +
            `(${[...new Set(doppelt)].slice(0, 3).join(", ")}).`,
        );
      }
    }

    // ── Offener Entwurf in der Zielumgebung ───────────────────────────────
    //
    // Der Unique-Index lässt je Lehrgang nur einen zu. Der Import könnte den
    // vorhandenen überschreiben – er tut es bewusst nicht: ein offener Entwurf
    // ist die Arbeit eines Menschen, und wessen sie ist, weiß hier niemand.
    if (target?.hasDraft) {
      blockers.push(
        `Dieser Lehrgang hat dort einen offenen Entwurf. Erst veröffentlichen oder ` +
          `verwerfen, dann noch einmal importieren.`,
      );
    }

    // ── Schlüssel, die verschwinden würden ────────────────────────────────
    //
    // Beim Nachimport der wichtige Fall, und bewusst nur eine WARNUNG - sie
    // bleibt es auch dann, wenn sie hier beantwortbar wäre. Zwei Gründe:
    //
    // Der Import legt einen Entwurf an und veröffentlicht nicht; ein Entwurf
    // darf Hinweise haben. Und ob ein fehlender Schlüssel wirklich schadet,
    // entscheidet sich an den Ergebnissen der ZIELUMGEBUNG - das prüft dort der
    // Trigger `curriculum_version_publish_check` beim Veröffentlichen. Wer diese
    // Warnung später zur Sperre macht, verbietet damit den Regelfall: eine
    // Lektion, die in Staging entfernt wurde, weil sie nie ein Kind gesehen hat.
    if (bundle && target?.publishedBundle) {
      const verloren = checkKeysKept(target.publishedBundle, bundle);
      if (verloren.length > 0) {
        warnings.push(
          `${verloren.length} Schlüssel der dort veröffentlichten Fassung ` +
            `${target.publishedVersion ?? "?"} fehlen in dieser Datei. Haben Kinder ` +
            `daran geübt, lässt sich der Entwurf nicht veröffentlichen.`,
        );
      }
    }

    // ── Der Vorgänger ─────────────────────────────────────────────────────
    if (entry.requiresSlug !== null) {
      const inDatei = file.curricula.some((c) => c.slug === entry.requiresSlug);
      if (!inDatei && !bySlug.has(entry.requiresSlug)) {
        warnings.push(
          `„Baut auf ${entry.requiresSlug}“ lässt sich nicht auflösen – der Lehrgang ` +
            `steht weder in dieser Datei noch dort. Er wird als „steht für sich“ angelegt.`,
        );
      }
    }

    const hints = bundle ? checkBundle(bundle).length + checkEditorial(bundle).length : 0;

    return {
      slug: entry.slug,
      title: entry.title,
      subtitle: entry.subtitle,
      position: entry.position,
      requiresSlug: entry.requiresSlug,
      sourceVersion: entry.sourceVersion,
      sourcePublishedAt: entry.sourcePublishedAt,
      bundle,
      blockers,
      warnings,
      hints,
      isNew: target === null,
      nextVersion: target?.nextVersion ?? 1,
      notes:
        `Übernommen aus ${file.source}, dort Fassung ${entry.sourceVersion} ` +
        `vom ${berlinDate(entry.sourcePublishedAt)}`,
    };
  });
}

/**
 * Auf welchen Lehrgang „baut auf“ nach dem Import zeigt – Slug → id.
 *
 * Der zweite Durchgang beim Schreiben. Getrennt, weil `requires_id` auf eine
 * Zeile zeigt, die es beim ersten Schreiben noch nicht gibt: hinge die
 * Reihenfolge an der Kette der Abhängigkeiten, sähe der Zyklus-Trigger eine halb
 * angelegte Kette.
 *
 * @param written Die Lehrgänge nach dem ersten Durchgang, Slug → id.
 */
export function requiresIdOf(
  entry: ImportEntry,
  written: ReadonlyMap<string, string>,
): string | null {
  if (entry.requiresSlug === null) return null;
  return written.get(entry.requiresSlug) ?? null;
}
